import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECG_VIEWER_CONFIG_STORE, DraggableModalWrapper } from '@/shared/ui';
import { EVENT_OTHER_TYPE } from '@/entities/event';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import { defaultControlHrSummaryModalValue } from '@/entities/heart-rate';
import SetMaxMinHrModal from '@/widgets/hr-summary/ui/modal/setMaxMinHrModal';
import useDailySummaryEcgViewerWrapper from './useDailySummaryEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type DailySummaryEcgViewerWrapperProps = {
  ecgDataMap: any;
  handleClickArrowButton: (isNext: boolean) => void;
  profileId?: any;
  studyId?: any;
  timezoneOffset?: any;
};

/**
 * Renders the ECG viewer of the daily heart-rate summary together with the set max/min HR modal. Falls back to a three-part skeleton while loading or when the ECG object has no samples, otherwise mounts AIECGViewer with daily-summary canvas ids, arrow scrolling, capture mode, pick-date, event and bookmark indicators, and hands the last selected column point to the max/min modal.
 *
 * @param props - Viewer inputs carrying the ECG data map, study and profile ids and the timezone offset.
 * @returns The daily summary ECG viewer with its max/min HR modal, or a skeleton while loading.
 */
const DailySummaryEcgViewerWrapper = ({
  ecgDataMap,
  handleClickArrowButton,
  profileId,
  studyId,
  timezoneOffset,
}: DailySummaryEcgViewerWrapperProps) => {
  const {
    isLoading,
    ecgObject,
    startStripEpoch,
    controlModalHrSummary,
    setControlModalHrSummary,
    selectedColumnInfo,
    ecgChartRef,
    pickDateAction,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    handlePickDate,
    clickArrowButtonHandle,
    handleUpdateBeat,
  } = useDailySummaryEcgViewerWrapper({ ecgDataMap, handleClickArrowButton, timezoneOffset });

  if (isLoading || !ecgObject?.dataSamples?.length || !startStripEpoch.current) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      <DraggableModalWrapper
        visible={controlModalHrSummary.isShowModalSetMaxMinHr}
        className="set-min-max-hr-draggable-modal-wrapper"
        ariaLabel="Set max/min heart rate"
      >
        <SetMaxMinHrModal
          chartRef={ecgChartRef}
          selectedPoint={selectedColumnInfo[selectedColumnInfo.length - 1]}
          onCloseModal={() => {
            setControlModalHrSummary({
              ...defaultControlHrSummaryModalValue,
              isShowModalSetMaxMinHr: false,
            });
          }}
          timezoneOffset={timezoneOffset}
        />
      </DraggableModalWrapper>
      <AIECGViewer
        ref={ecgChartRef}
        idComponents={{
          canvasBackground: 'cvBgDailySummary',
          exportGridCanvas: 'expGridCvDailySummary',
          canvasChart: 'cvChartDailySummary',
          canvasObject: 'cvObjDailySummary',
          canvasScroller: 'cvScrollerDailySummary',
          contextMenu: 'contextMenuDailySummary',
          tooltip: 'tooltipDailySummary',
          tab: BEAT_HR_TAB_ID,
        }}
        ecg={ecgObject}
        ecgDataMap={ecgDataMap}
        configChart={configChart}
        isAppliedAI
        isDisableAIAssist
        isHideFineGridMenu
        isHideMarkASampleMenu
        hasUpdateBeatContextMenu
        captureMode={getCaptureMode()}
        handleCaptureImages={handleCaptureImages}
        handlePickDate={handlePickDate}
        pickDateAction={pickDateAction}
        eventIndicator={eventIndicator}
        bookmarkIndicators={bookmarkIndicators}
        hasArrowButtonScroller
        handleClickArrowButton={clickArrowButtonHandle}
        handleUpdateBeat={handleUpdateBeat}
        timeObject={{
          startMoment: startStripEpoch.current,
          timezoneOffset,
        }}
        isInitialARBeatMode={ECG_VIEWER_CONFIG_STORE.isInitialARBeatMode}
        aiChannel={aiChannel}
        isDisableNextBtn={isDisableNextBtn}
        isDisablePrevBtn={isDisablePrevBtn}
        studyInfoObject={{
          studyId,
          profileId,
          type: EVENT_OTHER_TYPE,
        }}
      />
    </div>
  );
};

export default DailySummaryEcgViewerWrapper;
